<template>
  <div class="bg-white rounded-lg shadow p-6">
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <h2 class="text-xl font-semibold text-gray-800">Estadísticas por Agencia</h2>
      <div class="w-full sm:w-auto">
        <select v-model="selectedSeason" class="w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
          <option v-for="season in seasons" :key="season.value" :value="season.value">
            {{ season.label }}
          </option>
        </select>
      </div>
    </div>

    <!-- Gráficos responsivos -->
    <div class="mb-8 h-[300px] relative">
      <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
      <pie-chart
        v-if="windowWidth < 640"
        :chart-data="pieChartData"
        :options="pieChartOptions"
        :height="300"
      />
      <bar-chart
        v-else
        :chart-data="chartData"
        :options="chartOptions"
        :height="300"
      />
    </div>

    <!-- Tabla de estadísticas -->
    <div class="overflow-x-auto relative">
      <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 sm:px-2 py-3 sm:py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agencia</th>
            <th v-for="month in displayMonths" :key="month"
                class="px-3 sm:px-1 py-3 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <router-link
                :to="generateMonthLink(month)"
                class="text-blue-600 hover:text-blue-800">
                {{ monthNames[month].substring(0,3) }}
              </router-link>
            </th>
            <th class="px-3 sm:px-1 py-3 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="(agency, index) in statsData" :key="index"
              class="hover:bg-gray-50 transition-colors">
            <td class="px-6 sm:px-2 py-4 sm:py-2 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ agency.name }}
            </td>
            <td v-for="month in displayMonths" :key="`${agency.name}-${month}`"
                class="px-3 sm:px-1 py-4 sm:py-2 whitespace-nowrap text-sm text-center text-gray-500">
              {{ agency.monthlyStats[month] || 0 }}
            </td>
            <td class="px-3 sm:px-1 py-4 sm:py-2 whitespace-nowrap text-sm text-center font-semibold text-gray-900">
              {{ agency.total }}
            </td>
          </tr>
          <!-- Fila de totales -->
          <tr class="bg-gray-50 font-semibold">
            <td class="px-6 sm:px-2 py-4 sm:py-2 whitespace-nowrap text-sm text-gray-900">Total</td>
            <td v-for="month in displayMonths" :key="`total-${month}`"
                class="px-3 sm:px-1 py-4 sm:py-2 whitespace-nowrap text-sm text-center text-gray-900">
              {{ getTotalForMonth(month) }}
            </td>
            <td class="px-3 sm:px-1 py-4 sm:py-2 whitespace-nowrap text-sm text-center text-gray-900">
              {{ getGrandTotal() }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { Bar, Pie } from 'vue-chartjs'
import commonsApi from '@/api/commons'

export default {
  name: 'LmTravelStats',

  components: {
    BarChart: {
      extends: Bar,
      props: {
        chartData: {
          type: Object,
          required: true
        },
        options: {
          type: Object,
          required: true
        }
      },
      watch: {
        // Agregar un watcher para actualizar el gráfico cuando cambien los datos
        chartData: {
          handler () {
            this.renderChart(this.chartData, this.options)
          },
          deep: true
        }
      },
      mounted () {
        this.renderChart(this.chartData, this.options)
      }
    },
    PieChart: {
      extends: Pie,
      props: {
        chartData: {
          type: Object,
          required: true
        },
        options: {
          type: Object,
          required: true
        }
      },
      watch: {
        chartData: {
          handler () {
            this.renderChart(this.chartData, this.options)
          },
          deep: true
        }
      },
      mounted () {
        this.renderChart(this.chartData, this.options)
      }
    }
  },

  data () {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth()
    const defaultSeason = currentMonth >= 5
      ? `${currentYear}-${currentYear + 1}`
      : `${currentYear - 1}-${currentYear}`

    return {
      selectedSeason: defaultSeason,
      months: [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6],
      monthNames: {
        1: 'Enero',
        2: 'Febrero',
        3: 'Marzo',
        4: 'Abril',
        5: 'Mayo',
        6: 'Junio',
        7: 'Julio',
        8: 'Agosto',
        9: 'Septiembre',
        10: 'Octubre',
        11: 'Noviembre',
        12: 'Diciembre'
      },
      statsData: [],
      windowWidth: window.innerWidth,
      isLoading: false
    }
  },

  computed: {
    seasons () {
      const currentYear = new Date().getFullYear()
      return [
        { value: `${currentYear - 1}-${currentYear}`, label: `Temporada ${currentYear - 1}/${currentYear}` },
        { value: `${currentYear}-${currentYear + 1}`, label: `Temporada ${currentYear}/${currentYear + 1}` }
      ]
    },

    displayMonths () {
      return this.months
    },

    chartData () {
      return {
        labels: this.displayMonths.map(month => this.monthNames[month].substring(0, 3)),
        datasets: this.statsData.map((agency, index) => ({
          label: agency.name,
          data: this.displayMonths.map(month => agency.monthlyStats[month] || 0),
          backgroundColor: this.getColorForIndex(index),
          borderColor: this.getColorForIndex(index),
          borderWidth: 1
        }))
      }
    },

    chartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [{
            stacked: true,
            gridLines: {
              display: false
            }
          }],
          yAxes: [{
            stacked: true,
            gridLines: {
              display: false
            }
          }]
        },
        legend: {
          position: 'bottom'
        },
        title: {
          display: true,
          text: `Distribución mensual por agencia - Temporada ${this.selectedSeason}`
        }
      }
    },

    pieChartData () {
      return {
        labels: this.statsData.map(agency => `${agency.name} (${agency.total})`),
        datasets: [{
          data: this.statsData.map(agency => agency.total),
          backgroundColor: this.statsData.map((_, index) => this.getColorForIndex(index)),
          borderWidth: 1
        }]
      }
    },

    pieChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom'
        },
        title: {
          display: true,
          text: `Total por agencia - Temporada ${this.selectedSeason}`
        }
      }
    }
  },

  methods: {
    getTotalForMonth (month) {
      return this.statsData.map(agency => agency.monthlyStats[month] || 0).reduce((acc, total) => acc + total, 0)
    },

    getGrandTotal () {
      return this.statsData.map(agency => agency.total).reduce((acc, total) => acc + total, 0)
    },

    getColorForIndex (index) {
      const baseColors = [
        '#4F46E5', // Indigo
        '#10B981', // Emerald
        '#F59E0B', // Amber
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#EC4899', // Pink
        '#06B6D4', // Cyan
        '#14B8A6', // Teal
        '#F97316', // Orange
        '#A855F7', // Violet
        '#6366F1', // Indigo-600
        '#84CC16' // Lime
      ]

      if (index < baseColors.length) {
        return baseColors[index]
      }

      // Genera un color hexadecimal aleatorio
      const randomColor = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`
      return randomColor
    },

    async fetchStats () {
      this.isLoading = true
      try {
        const [startYear, endYear] = this.selectedSeason.split('-')
        const response = await commonsApi.axiosPostWrapper('/programs/season', {
          startYear,
          endYear
        })

        // Combinar datos duplicados por nombre de agencia
        const agenciesMap = new Map()
        response.data.flatMap(yearData =>
          yearData.agencies.forEach(agency => {
            if (agenciesMap.has(agency.name)) {
              const existing = agenciesMap.get(agency.name)
              Object.keys(agency.monthlyStats).forEach(month => {
                existing.monthlyStats[month] = (existing.monthlyStats[month] || 0) + (agency.monthlyStats[month] || 0)
              })
              existing.total += agency.total
            } else {
              agenciesMap.set(agency.name, {
                name: agency.name,
                monthlyStats: { ...agency.monthlyStats },
                total: agency.total
              })
            }
          })
        )

        this.statsData = Array.from(agenciesMap.values())
        this.monthlyTotals = response.data.reduce((acc, yearData) => {
          Object.keys(yearData.monthlyTotals).forEach(month => {
            acc[month] = (acc[month] || 0) + yearData.monthlyTotals[month]
          })
          return acc
        }, {})
        this.yearTotal = response.data.reduce((acc, yearData) => acc + yearData.yearTotal, 0)
      } catch (error) {
        console.error('Error fetching stats:', error)
      } finally {
        this.isLoading = false
      }
    },

    handleResize () {
      this.windowWidth = window.innerWidth
    },

    generateMonthLink (month) {
      // Determinar el año basado en el mes y la temporada seleccionada
      const [startYear, endYear] = this.selectedSeason.split('-')
      const year = month >= 6 ? startYear : endYear

      // Crear fecha para el primer día del mes
      const date = new Date(year, month - 1, 3)

      // Generar el query string codificado
      const queryParams = {
        currentDate: date.toISOString(),
        currentView: 'Month'
      }
      const encodedQuery = encodeURIComponent(JSON.stringify(queryParams))

      return `/programs/todos?cq=${encodedQuery}`
    }
  },

  watch: {
    selectedSeason () {
      this.fetchStats()
    }
  },

  mounted () {
    this.fetchStats()
    window.addEventListener('resize', this.handleResize)
  },

  destroyed () {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>
